/* eslint eqeqeq: "off" */
import React, { memo } from 'react';
import { centsToDollar, aprToPercent, sortDollarColumn, promosToChips } from '../../utils/formatters';
import EditableTextCell from './EditableTextCell';
import { LoanService } from '../../services';
import MUIDataTable from 'mui-datatables';
import Tooltip from "@material-ui/core/Tooltip";
import PlayCircleIcon from '@material-ui/icons/PlayCircleOutline';
import IconButton from "@material-ui/core/IconButton";
import { DealerMetricTooltip, LoanMetricTooltip, BorrowerMetricTooltip } from '../../components';
import { isHeldCell, dealerNeedsReviewCell } from '../../utils/cellRenderers';
import { FlagCell } from '../../components';
import { useRole } from '../../contexts/RolesContext';
import Chip from '@material-ui/core/Chip';
import red from '@material-ui/core/colors/red';

const RunRulesButton = ({ ruleFunction }) => {

  const handleRunRulesClick = () => {
      ruleFunction();
  }
  return (
        <Tooltip title={"Run Rules"}>
          <IconButton onClick={handleRunRulesClick}>
            <PlayCircleIcon/>
          </IconButton>
        </Tooltip>
  );
}


const getTableOptions = (ruleFunction) => {
  return {
      serverSide: true,
      filter: false,
      selectableRows: 'none',
      print: false,
      rowsPerPage: 50,
      rowsPerPageOptions: [50,100,250],
      download: false,
      sort: false,
      search: false,
      customToolbar: () => {
        return (
          <RunRulesButton ruleFunction={ruleFunction}/>
        );
      }
    }
};

const tablifyFailures = (ruleFailures, isLoading) =>
  ruleFailures?.map(ruleFailure => {
      return [
        ruleFailure.id,
        ruleFailure.loan.loanNumber,
        ruleFailure.loan.dealer.dealerNumber,
        ruleFailure.loan.dealer.name,
        centsToDollar(ruleFailure.loan.totalLoanAmountCents),
        (ruleFailure.loan.hasStagedFunding === true ? 'Yes' : 'No'),
        ruleFailure.loan.isHeld,
        ruleFailure.loan.dealer.organization,
        ruleFailure.loan.dealer.needsReview,
        ruleFailure.loan.lender?.shortName,
        ruleFailure.failureReasons,
        centsToDollar(ruleFailure.loan.estimatedProjectAmountCents),
        centsToDollar(ruleFailure.loan.totalLoanAmountCents),
        ruleFailure.loan.termMonths,
        aprToPercent(ruleFailure.loan.apr),
        promosToChips(ruleFailure.loan),
        ruleFailure.loan.applicant.fullName,
        ruleFailure.loan.applicant.creditScore,
        ruleFailure.loan.applicant.employer,
        centsToDollar(ruleFailure.loan.fundingRequest?.totalIncome),
        aprToPercent(ruleFailure.loan.fundingRequest?.debtIncomeRatio),
        ruleFailure.loan.coapplicant?.fullName,
        ruleFailure.loan.coapplicant?.employer,
        ruleFailure.loan.coapplicant?.creditScore,
        (isNaN(parseInt(ruleFailure.loan.dealer.dealerMetric.totalFundedLoans)) ? 0 : parseInt(ruleFailure.loan.dealer.dealerMetric.totalFundedLoans)),
        ruleFailure.loan.dealer.dealerMetric.maxLoanAmount,
        centsToDollar(ruleFailure.loan.dealer.dealerMetric.totalLoanAmount),
        ruleFailure.loan.dealer.dealerMetric.averageLoanAmount,
        ruleFailure.loan.dealer.dealerMetric.firstFundingDate,
        ruleFailure.loan.dealer.dealerMetric.lastFundingDate,
        (isNaN(parseInt(ruleFailure.loan.dealer.dealerMetric.delinquentLoansCount)) ? 0 : parseInt(ruleFailure.loan.dealer.dealerMetric.delinquentLoansCount)),
        (isNaN(parseInt(ruleFailure.loan.dealer.dealerMetric.delinquentLoansOverThirtyDaysCount)) ? 0 : parseInt(ruleFailure.loan.dealer.dealerMetric.delinquentLoansOverThirtyDaysCount)),
        ruleFailure.loan.dealer.isConcierge,
        ruleFailure.loan.notes,
        ruleFailure.loan.dealer.isManualFlagged,
        ruleFailure.loan.dealer.dealerMetric.allTimeAverageCreditScorePb,
        ruleFailure.loan.dealer.dealerMetric.lastYearAverageCreditScorePb,
        ruleFailure.loan.dealer.manualFlaggedReason
      ];
});

const RulesTable = ({ruleFilters, ruleFailures, selectedDate, ruleFunction}) => {

  const { isDailyProcessingWrite, isAdmin } = useRole();

  const columns = [
    {
      name: 'ID',
      options: {
        display: false,
        viewColumns: false,
      }
    },
    {
      name: 'Loan ID',
      options: {
          filter: false,
          customBodyRender: (value, tableMeta) => {
            return <React.Fragment>
              <div style={{display: 'flex'}}>
                <LoanMetricTooltip
                  loanId={value}
                  estimatedProjectAmount={tableMeta.rowData[11]}
                  totalLoanAmount={tableMeta.rowData[12]}
                  loanTerm={tableMeta.rowData[13]}
                  loanApr={tableMeta.rowData[14]}
                  promos={tableMeta.rowData[15]}
                /><BorrowerMetricTooltip
                  borrowerName={tableMeta.rowData[16]}
                  borrowerCreditScore={tableMeta.rowData[17]}
                  borrowerDTI={tableMeta.rowData[20]}
                  borrowerEmployer={tableMeta.rowData[18]}
                  borrowerIncome={tableMeta.rowData[19]}
                  coborrowerName={tableMeta.rowData[21]}
                  coborrowerCreditScore={tableMeta.rowData[23]}
                  coborrowerEmployer={tableMeta.rowData[22]}
                />
              </div>
            </React.Fragment>
          }
      }
    },
    {
      name: 'Dealer #',
      options: {
        filter: false,
        customBodyRender: (value, tableMeta) => {
          return <a href={`/dealers/${value}`}>{value}</a>
        }
      }
    },
    {
      name: 'Dealer',
      options: {
        customBodyRender: (value, tableMeta) => {
          return <DealerMetricTooltip
            dealerName={value}
            totalFundedLoans={tableMeta.rowData[24]}
            maxLoanAmount={centsToDollar(tableMeta.rowData[25])}
            totalLoanAmount={tableMeta.rowData[26]}
            averageLoanAmount={centsToDollar(tableMeta.rowData[27])}
            firstFundingDate={tableMeta.rowData[28]}
            lastFundingDate={tableMeta.rowData[29]}
            dealerConcierge={tableMeta.rowData[32] ? "Yes" : "No"}
            dealerDelinquentLoans={tableMeta.rowData[30]}
            dealerThirtyDayDelinquentLoansCount={tableMeta.rowData[31]}
            dealerAllTimeAverageCreditScore={tableMeta.rowData[35]}
            dealerLastYearAverageCreditScore={tableMeta.rowData[36]}
          />
        }
      }
    },
    {
      name: 'Amount',
      options: {
          filter: false,
          sortCompare: sortDollarColumn,
      }
    },
    {
      name: 'Staged Funding',
    },
    {
      name: 'On Hold?',
      options: {
        customBodyRender: isHeldCell
      }
    },
    {
      name: 'Org',
    },
    {
      name: 'Dealer Account Flag',
      options: {
        customBodyRender: dealerNeedsReviewCell
      }
    },
    {
      name: 'Lender',
    },
    {
      name: 'Failures',
      options: {
        filterOptions: {
          filter: true,
          logic: (values, filters) => {
            return values.name === filters[0];
          }
        },
        customBodyRender: (failureList) => {
          return failureList.map((failure) => {
            return <Chip 
              label={failure?.toUpperCase()} 
              style={{fontWeight: 'bold', fontSize: '12px', backgroundColor: red[500]}}
            />
          })
        }
      }
    },
    {
      name: 'estimatedProjectAmount',
      options: {
        filter: false,
        display: false,
        viewColumns: false,
      }
    },
    {
      name: 'totalLoanAmount',
      options: {
        filter: false,
        display: false,
        viewColumns: false,
      }
    },
    {
      name: 'term',
      options: {
        filter: false,
        display: false,
        viewColumns: false,
      }
    },
    {
      name: 'apr',
      options: {
        filter: false,
        display: false,
        viewColumns: false,
      }
    },
    {
      name: 'promos',
      options: {
        filter: false,
        display: false,
        viewColumns: false,
      }
    },
    {
      name: 'Borrower',
      options: {
        filter: false,
        display: false,
        viewColumns: false,
      }
    },
    {
      name: 'Borrower credit score',
      options: {
        filter: false,
        display: false,
        viewColumns: false,
      }
    },
    {
      name: 'Borrower Employer',
      options: {
        filter: false,
        display: false,
        viewColumns: false,
      }
    },
    {
      name: 'totalIncome',
      options: {
        filter: false,
        display: false,
        viewColumns: false,
      }
    },
    {
      name: 'debtincomeratio',
      options: {
        filter: false,
        display: false,
        viewColumns: false,
      }
    },
    {
      name: 'Coborrower',
      options: {
        filter: false,
        display: false,
        viewColumns: false,
      }
    },
    {
      name: 'Coborrower employer',
      options: {
        filter: false,
        display: false,
        viewColumns: false,
      }
    },
    {
      name: 'Coborrower credit score',
      options: {
        filter: false,
        display: false,
        viewColumns: false,
      }
    },
    {
      name: 'totalFundedLoans',
      options: {
        filter: false,
        display: false,
        viewColumns: false,
      }
    },
    {
      name: 'maxLoanAmount',
      options: {
        filter: false,
        display: false,
        viewColumns: false,
      }
    },
    {
      name: 'totalLoanAmount',
      options: {
        filter: false,
        display: false,
        viewColumns: false,
      }
    },
    {
      name: 'averageLoanAmount',
      options: {
        filter: false,
        display: false,
        viewColumns: false,
      }
    },
    {
      name: 'firstFundingDate',
      options: {
        filter: false,
        display: false,
        viewColumns: false,
      }
    },
    {
      name: 'lastFundingDate',
      options: {
        filter: false,
        display: false,
        viewColumns: false,
      }
    },
    {
      name: 'Dealer Delinquencies',
      options: {
        filter: false,
        display: true,
        viewColumns: true,
        customBodyRender: (value, tableMeta) => {
          return `${value} (${tableMeta.rowData[24] == 0 ? 0 : Math.trunc((value / tableMeta.rowData[24]) * 100)}%)`;
        }
      }
    },
    {
      name: 'Dealer Delinquencies For 30+ Days:',
      options: {
        filter: false,
        display: true,
        viewColumns: true,
        customBodyRender: (value, tableMeta) => {
          return `${value} (${tableMeta.rowData[24] == 0 ? 0 : Math.trunc((value / tableMeta.rowData[24]) * 100)}%)`;
        }
      }
    },
    {
      name: 'concierge',
      options: {
        filter: false,
        display: false,
        viewColumns: false,
      }
    },
    {
      name: 'Notes',
      options: {
          setCellProps: () => ({ style: { minWidth: "250px" }}),
          display: true,
          customBodyRender: (value, tableMeta) => {
            return ( (isDailyProcessingWrite() || isAdmin()) ?
            <EditableTextCell
              value={value}
              id={tableMeta.rowData[1]}
              label={'Funding Request Notes'}
              onSave={LoanService.updateLoanNotes} /> : value
            );
        }
      }
    },
    {
      name: "Manual Flag",
      options: {
        customBodyRender: (value, tableMeta) => {
          return ( isDailyProcessingWrite() ?
            <FlagCell value={value} dealerNumber={tableMeta.rowData[2]} flagReason={tableMeta.rowData[37]} /> :
            <FlagCell value={value} dealerNumber={tableMeta.rowData[2]} flagReason={tableMeta.rowData[37]} disableUpdates={true} />
          );
        }
      }
    },
    {
      name: "All Time Average Credit Score",
      options: {
        display: false,
        filter: false,
        viewColumns: false,
      }
    },
    {
      name: "Last Year Average Credit Score",
      options: {
        display: false,
        filter: false,
        viewColumns: false,
      }
    }
  ];

    return ruleFailures == null ? (
        <h1>Loading...</h1>
      ) : (
        <MUIDataTable data={tablifyFailures(ruleFailures)} columns={columns} options={getTableOptions(ruleFunction)} />
      );
    
}

export default memo(RulesTable);